import L from "leaflet";
import { Coords } from "types/app";

/** A waypoint farther than this (in meters) from the route is joined to it by a dashed line */
const MISSING_ROUTE_TOLERANCE = 10;

/** The index of the route's point closest to the point (the last one of equally close points) */
export const getClosestRoutePointIndex = (
  coordinates: Coords[],
  point: Coords | L.LatLng
) => {
  const latLng = L.latLng(point);
  let closestIndex = 0;
  let minDistance = Infinity;
  for (let index = coordinates.length - 1; index >= 0; index -= 1) {
    const distance = latLng.distanceTo(coordinates[index]);
    if (distance < minDistance) {
      closestIndex = index;
      minDistance = distance;
    }
  }
  return closestIndex;
};

/**
 * The index at which a waypoint is inserted when the route is pressed at the point: after the last waypoint
 * whose place on the route is before the pressed one. Always between two waypoints.
 */
export const getInsertedWaypointIndex = (
  coordinates: Coords[],
  waypoints: Coords[],
  point: Coords | L.LatLng
) => {
  const pressedIndex = getClosestRoutePointIndex(coordinates, point);
  const waypointIndices = waypoints.map((waypoint) =>
    getClosestRoutePointIndex(coordinates, waypoint)
  );
  const waypointBefore = waypointIndices.reduce(
    (before, routeIndex, waypointIndex) =>
      routeIndex <= pressedIndex ? waypointIndex : before,
    -1
  );
  return Math.min(Math.max(waypointBefore + 1, 1), waypoints.length - 1);
};

/**
 * Lines from the waypoints to the route where the route doesn't reach them, e.g. a waypoint in a field is
 * routed from the nearest road
 */
export const getMissingRouteSegments = (
  coordinates: Coords[],
  waypoints: Coords[]
): [Coords, Coords][] =>
  coordinates.length === 0
    ? []
    : waypoints.flatMap((waypoint) => {
        const routePoint =
          coordinates[getClosestRoutePointIndex(coordinates, waypoint)];
        return L.latLng(waypoint).distanceTo(routePoint) >
          MISSING_ROUTE_TOLERANCE
          ? [[waypoint, routePoint]]
          : [];
      });
